import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import BarcodeScanner from "./BarcodeScanner";
import { nfcWriteSupported, nfcReadOnce, nfcErrorInfo, tokenFromUrl } from "../lib/nfc";
import { Broadcast, QrCode, MagnifyingGlass, Spinner, ListMagnifyingGlass } from "@phosphor-icons/react";

// Reusable Scan (NFC tap OR QR) / Select picker. Resolves a scanned tag token → its Asset via the
// single /t/{token} tag record, so NFC & QR both land on the same asset. onPick receives the full
// asset object (so callers get weights/registration etc for advisory calculations).
export default function AssetPicker({ open, title = "Choose item", description, onPick, onClose, excludeId }) {
  const [mode, setMode] = useState("choose"); // choose | select | nfc
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(false);
  const nfcSupported = nfcWriteSupported();

  useEffect(() => { if (open) { setMode("choose"); setQ(""); setList([]); setQr(false); } }, [open]);

  useEffect(() => {
    if (mode !== "select") return;
    const t = setTimeout(() => {
      api.get(`/assets${q ? `?q=${encodeURIComponent(q)}` : ""}`)
        .then((r) => setList((r.data || []).filter((a) => a.id !== excludeId)))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, mode, excludeId]);

  const resolveToken = async (raw) => {
    const tok = tokenFromUrl(raw);
    if (!tok) { toast.error("That doesn't look like a TMIC tag."); return; }
    setBusy(true);
    try {
      // /t/{token}/resolve is available to any authenticated user (drivers included) and lands on
      // the SAME asset record for NFC or QR — so scanning works inside a normal vehicle check.
      const r = await api.get(`/t/${encodeURIComponent(tok)}/resolve`);
      if (r.data?.target_type !== "asset" || !r.data?.asset) { toast.error("That tag isn't linked to an asset."); return; }
      const a = r.data.asset;
      if (a.id === excludeId) { toast.error("That's the same vehicle — pick a different item."); return; }
      onPick(a);
    } catch (e) {
      const s = e.response?.status;
      toast.error(s === 404 ? "Tag not recognised." : s === 410 ? "That tag is no longer active." : "Couldn't resolve that tag.");
    } finally { setBusy(false); }
  };

  const scanNfc = async () => {
    setMode("nfc");
    try { const { url } = await nfcReadOnce({}); await resolveToken(url); }
    catch (e) { toast.error(nfcErrorInfo(e).message); }
    finally { setMode("choose"); }
  };

  return (
    <>
      <Dialog open={open && !qr} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="bg-white max-w-md" data-testid="asset-picker" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ListMagnifyingGlass size={20} weight="bold" />{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>

          {mode === "choose" && (
            <div className="flex flex-col gap-3">
              <Button data-testid="picker-scan-nfc" disabled={!nfcSupported} onClick={scanNfc}
                className="h-14 bg-blue-600 hover:bg-blue-700 font-bold uppercase disabled:opacity-50">
                <Broadcast size={20} weight="bold" className="mr-2" />Scan NFC Tag
              </Button>
              <Button data-testid="picker-scan-qr" onClick={() => setQr(true)}
                className="h-14 bg-slate-800 hover:bg-slate-900 font-bold uppercase">
                <QrCode size={20} weight="bold" className="mr-2" />Scan QR Code
              </Button>
              <Button data-testid="picker-select" variant="outline" onClick={() => setMode("select")}
                className="h-14 font-bold uppercase border-2">
                <MagnifyingGlass size={20} weight="bold" className="mr-2" />Select from list
              </Button>
              {!nfcSupported && <p className="text-xs text-slate-400 text-center">NFC tap isn't available on this device — use QR or the list.</p>}
            </div>
          )}

          {mode === "nfc" && (
            <div className="text-center py-8" data-testid="picker-nfc-waiting">
              <Spinner size={44} weight="bold" className="text-blue-600 mx-auto animate-spin" />
              <p className="font-display text-lg font-black text-slate-900 mt-3 animate-pulse">Hold the tag to the phone…</p>
              <Button variant="ghost" className="mt-3 font-semibold" onClick={() => setMode("choose")}>Cancel</Button>
            </div>
          )}

          {mode === "select" && (
            <div className="flex flex-col gap-2">
              <Input data-testid="picker-search" autoFocus placeholder="Search name, number, reg…" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 border-2" />
              <div className="max-h-72 overflow-y-auto flex flex-col gap-1" data-testid="picker-results">
                {list.length === 0 ? <p className="text-sm text-slate-400 p-2">No matching items.</p> :
                  list.map((a) => (
                    <button key={a.id} type="button" data-testid={`picker-item-${a.asset_number}`} disabled={busy}
                      onClick={() => onPick(a)}
                      className="text-left border-2 border-slate-200 rounded-lg px-3 py-2 hover:border-blue-500 transition-colors duration-100">
                      <div className="font-body font-semibold text-slate-800 text-sm">{a.name}</div>
                      <div className="font-mono text-xs text-slate-500">{a.asset_number}{a.registration ? ` · ${a.registration}` : ""}</div>
                    </button>
                  ))}
              </div>
              <Button variant="ghost" className="font-semibold" onClick={() => setMode("choose")}>Back</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {qr && <BarcodeScanner title="Scan a TMIC QR code" preferRear
        onDetected={(raw) => { setQr(false); resolveToken(raw); }} onClose={() => setQr(false)} />}
    </>
  );
}
